import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ContactModal from '../../../components/feature/ContactModal';
import SEO from '../../../components/feature/SEO';

export default function Consultants() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    contactName: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-affiliate-working`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          type: 'Consultant'
        }),
      });

      const result = await response.json();
      console.log('Email response:', result);

      if (result.success) {
        setSubmitMessage('✅ Application submitted successfully! We will contact you within 48 hours.');
        // Reset form
        setFormData({
          contactName: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          notes: ''
        });
      } else {
        setSubmitMessage(`❌ Failed to submit application: ${result.error}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitMessage('❌ Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="XeroVerifier Consultant Network – Elevate Your Practice with Invoice Security"
        description="Join XeroVerifier's consultant network. Demonstrate proactive risk management expertise, build deeper client relationships, and deliver measurable invoice fraud protection solutions."
        keywords="Xero consultant program, invoice fraud consultant, XeroVerifier consultant network, business security consulting, Xero security advisor"
        canonical="/affiliates/consultants"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'XeroVerifier Consultant Partner Program',
          description: 'Consultant partner program for business advisors who want to add invoice fraud protection to their service offering.',
          url: `${import.meta.env.VITE_SITE_URL || ''}/affiliates/consultants`,
          publisher: {
            '@type': 'Organization',
            name: 'XeroVerifier',
          },
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Elevate Your Consulting Practice
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Strengthen your client relationships by recommending XeroVerifier - the essential security 
                solution that protects businesses from invoice fraud and builds customer trust.
              </p>
              <button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-briefcase-line mr-2"></i>
                Join Our Consultant Network
              </button>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20consultant%20presenting%20security%20solutions%20to%20executive%20team%20in%20modern%20boardroom%2C%20showing%20invoice%20verification%20system%20on%20presentation%20screen%2C%20strategic%20consulting%20meeting%2C%20expertise%20and%20innovation%20atmosphere&width=600&height=500&seq=consultant-hero&orientation=landscape"
                alt="Consultant presenting to executives"
                className="rounded-xl shadow-2xl w-full object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Consultants Recommend XeroVerifier
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Demonstrate your expertise by introducing clients to innovative solutions that solve real business problems.
            </p>
          </div>
          
          <div className="space-y-20">
            {/* Benefit 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Showcase Strategic Thinking
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  Position yourself as a forward-thinking consultant who identifies emerging risks and 
                  proactively recommends solutions before problems occur.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-lightbulb-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Demonstrate proactive risk management expertise</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-eye-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Show awareness of current fraud trends</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-star-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Build reputation as an innovative advisor</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20DSLR%20photography%20of%20two%20business%20professionals%20reviewing%20financial%20data%20and%20risk%20charts%20on%20a%20large%20monitor%20in%20a%20modern%20office%2C%20both%20people%20shown%20from%20waist%20up%20with%20natural%20relaxed%20postures%2C%20sharp%20highly%20detailed%20natural%20human%20faces%20with%20realistic%20skin%20pores%20and%20texture%2C%20believable%20hair%20and%20natural%20lighting%20with%20soft%20shadows%2C%20corporate%20stock%20photo%20aesthetic%2C%20no%20illustrated%20or%20artificial%20look%2C%20candid%20authentic%20business%20meeting&width=600&height=400&seq=strategic-thinking-real&orientation=landscape"
                  alt="Strategic consulting"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Two%20business%20professionals%20standing%20side%20by%20side%20in%20a%20modern%20office%2C%20a%20male%20consultant%20and%20a%20female%20client%20with%20darker%20skin%2C%20both%20fully%20visible%20from%20head%20to%20waist%20with%20no%20desk%20or%20table%20in%20front%2C%20smiling%20at%20a%20laptop%20screen%20showing%20growth%20charts%2C%20hands%20relaxed%20at%20their%20sides%20with%20all%20fingers%20intact%2C%20bright%20office%20background%2C%20clean%20correct%20anatomy%2C%20editorial%20business%20photography&width=600&height=400&seq=measurable-results-v2&orientation=landscape&nocache=true"
                  alt="Measurable results"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Deliver Measurable Value
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  Provide clients with tangible security improvements they can measure and report to stakeholders, 
                  proving the ROI of your consulting engagement.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-bar-chart-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Quantifiable fraud prevention metrics</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-shield-check-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Improved client trust and reputation scores</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-money-dollar-circle-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Reduced costs from payment disputes</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Strengthen Client Relationships
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  Build deeper, longer-lasting relationships by introducing solutions that protect your 
                  clients' most valuable asset - their reputation and customer trust.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-heart-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Increase client loyalty and retention</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-user-follow-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Generate referrals from satisfied clients</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-star-line text-purple-500 text-xl"></i>
                    <span className="text-gray-700">Position yourself as a trusted advisor</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Waist-up%20photo%20of%20exactly%20two%20people%2C%20one%20businessman%20on%20the%20left%20in%20a%20navy%20suit%20and%20one%20businesswoman%20on%20the%20right%20with%20darker%20skin%20wearing%20a%20professional%20blazer%2C%20standing%20facing%20each%20other%20and%20sharing%20a%20single%20natural%20right-hand%20handshake%2C%20both%20full%20upper%20bodies%20clearly%20visible%2C%20friendly%20smiles%2C%20bright%20modern%20office%20lobby%20background%2C%20realistic%20editorial%20business%20photography%2C%20clean%20correct%20anatomy&width=600&height=400&seq=client-handshake-v2&orientation=landscape&nocache=true"
                  alt="Client relationships"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Simple Implementation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Easy to recommend, simple to implement, and immediate value for your clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Assess Client Needs</h3>
              <p className="text-gray-600">
                Identify clients who send invoices and could benefit from enhanced security and trust-building measures.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Present Solution</h3>
              <p className="text-gray-600">
                Use our professional materials to demonstrate how XeroVerifier addresses their security and trust challenges.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Support Implementation</h3>
              <p className="text-gray-600">
                Guide the setup process and help clients realize immediate benefits from their new security measures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Consultant Resources */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Consultant Resources & Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to successfully recommend and implement XeroVerifier for your clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Presentation Materials - temporarily commented out
            <div className="bg-white p-6 rounded-xl shadow-lg text-center border-2 border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-presentation-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Presentation Materials</h3>
              <p className="text-gray-600 text-sm">Professional slide decks and demo materials</p>
            </div>
            */}
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center border-2 border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Case Studies</h3>
              <p className="text-gray-600 text-sm">Real-world success stories and ROI examples</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center border-2 border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-headphone-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Technical Support</h3>
              <p className="text-gray-600 text-sm">Direct access to our technical team</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center border-2 border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Referral Rewards</h3>
              <p className="text-gray-600 text-sm">Earn rewards for successful client referrals</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Enhance Your Consulting Practice Today
              </h2>
              <p className="text-xl mb-8">
                Join our consultant network and start offering your clients the security solutions they need to thrive.
              </p>
              <button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-rocket-line mr-2"></i>
                Start Recommending XeroVerifier
              </button>
            </div>
            
            {/* Application Form */}
            <div className="bg-white p-8 rounded-xl shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Apply Now</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    placeholder="Full Name *"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address *"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Company Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Tell us about your consulting practice and how you'd like to recommend XeroVerifier..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 resize-vertical"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.notes.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Submit Application
                    </>
                  )}
                </button>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg ${
                    submitMessage.includes('error') 
                      ? 'bg-red-50 border border-red-200 text-red-700' 
                      : 'bg-green-50 border border-green-200 text-green-700'
                  }`}>
                    <div className="flex items-center">
                      <i className={`${
                        submitMessage.includes('error') 
                          ? 'ri-error-warning-line text-red-500' 
                          : 'ri-check-circle-line text-green-500'
                      } text-xl mr-3`}></i>
                      <p>{submitMessage}</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Join Our Consultant Network"
        subtitle="Let's explore how XeroVerifier can enhance your practice"
      />
    </div>
  );
}